// React i hooks
import { memo, useCallback, useEffect, useState } from "react";

// API servisi
import {
  createContactNoteAPI,
  getContactNoteAPI,
  updateContactNoteAPI,
} from "services/api/contacts/api";
import { ContactNoteEditFormValues } from "services/api/contacts/types";
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";
import { initialContactNoteEditForm } from "services/api/contacts/consts";
import { useHandleTextFieldChange } from "services/forms/hooks";

// Tipovi i konstante
import { ContactNoteEditProps } from "./types";
import { CONTACT_NOTE_EDIT_FORM_SCHEMA } from "./validation";

// UI komponente
import { Button } from "ui/components/Button/Button";
import { Loader } from "ui/components/Loader";
import { CheckBox } from "ui/components/CheckBox";
import { TextArea } from "ui/components/TextField/TextArea";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { useTranslate } from "helpers";
import { useDialog } from "ui/modules/public/components/DialogProvider/DialogProvider";

const ContactNoteEditCard: React.FC<ContactNoteEditProps> = (props) => {
  const { noteId, contactId, show, onSuccess, onClose } = props;

  const { t } = useTranslate();
  const { createDialog } = useDialog();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [formValues, setFormValues] = useState<ContactNoteEditFormValues>(
    initialContactNoteEditForm
  );

  useEffect(() => {
    if (show) {
      fetchNote();
    }
  }, [show, noteId]);

  const fetchNote = async () => {
    if (!noteId) {
      setFormValues(initialContactNoteEditForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getContactNoteAPI(contactId, noteId);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);

    setErrors({});
  };

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(
      formValues,
      CONTACT_NOTE_EDIT_FORM_SCHEMA,
      setErrors
    );

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    try {
      let result;
      if (!!noteId) {
        result = await updateContactNoteAPI(contactId, noteId, formValues);
      } else {
        result = await createContactNoteAPI(contactId, formValues);
      }
      if (result && onSuccess) onSuccess();
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  const handleCheckBoxChange = useCallback(() => {
    setFormValues((old) => ({ ...old, important: !old.important }));
  }, []);

  const handleCloseModal = () => {
    createDialog({
      onConfirm: onClose
    });
  };

  return (
    <Modal show={show} onHide={onClose} position="centered" size="small">
      <ModalHeader icon="/media/images/add-icon.svg"   title={noteId ? t("admin.contact.phone.note.edit.title") : t("admin.contact.phone.note.add.title")} onHide={onClose} />
      <ModalBody>
        <div className="flex gap-4">
          <div className="w-full m-auto">
            <div className="flex gap-3 h-full">
              <form noValidate className="text-left flex-1 p-4">
                <div className="">
                  <div className="flex gap-3 mb-3">
                    <div className="flex-1">
                      <TextArea
                        name="content"
                        label={t("admin.contact.note.add.content")}
                        placeholder={t("admin.contact.note.add.content.placeholder")}
                        onChange={handleTextFieldChange}
                        error={errors.content}
                        value={formValues.content}
                        required
                        endIcon=""
                      ></TextArea>
                    </div>
                  </div>
                  <div className="flex gap-3 mb-3">
                    <div className="flex-1">
                      <CheckBox
                        name="important"
                        onChange={handleCheckBoxChange}
                        checked={Boolean(formValues.important)}
                        label={t("admin.contact.note.add.important")}
                        error={errors.important}
                      ></CheckBox>
                    </div>
                  </div>
                </div>
                <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
                  <Button
                    variant="outlined"
                    children={t("admin.close")}
                    onClick={handleCloseModal}
                  />
                  <Button
                    variant="primary"
                    children={t("admin.save")}
                    onClick={handleFormSubmit}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>

        <Loader isLoading={isLoading ?? false}> </Loader>
      </ModalBody>
    </Modal>
  );
};
export default memo(ContactNoteEditCard);
